import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  GetAnalysisPipelineStep,
  isSidebarDatasetInitializationSingleOutputStepType,
} from 'analysis-tool/AnalysisTypes';
import {isStepInProgress} from 'analysis-tool/isAnalysisRunning';
import {assertAndContinue} from 'Asserts';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FONT_PROXIMA_13, FONT_PROXIMA_16} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {NEAR_BLACK} from 'ui-library/Colors';

import AnalysisRunningAnimation from '../components/AnalysisRunningAnimation';
import {AnalysisMainViewItemType, MainViewComponentProps} from './MainViewTypes';

const DEFAULT_MESSAGE = {
  title: 'Your analysis is running...',
  subtitle: 'This could take a moment to complete.',
};

function getMessages(step: GetAnalysisPipelineStep): {title: string; subtitle: string | null} {
  if (isSidebarDatasetInitializationSingleOutputStepType(step.definition.stepType)) {
    return {
      title: 'Creating Table',
      subtitle: 'This could take several minutes.',
    };
  }

  switch (step.definition.stepType) {
    case PipelineStepType.SAVE_TO_RESULTS:
      return {
        title: 'Creating Results',
        subtitle: 'This could take several minutes.',
      };
    case PipelineStepType.PROCEDURE_RUN_DATA_FRAMES:
      return {
        title: 'Creating Tables',
        subtitle: 'This could take several minutes.',
      };
    default:
      return DEFAULT_MESSAGE;
  }
}

/**
 * Component to show the view of steps that have the `RUNNING` `PipelineStatusCode`.
 *
 * To show custom text for your step type, modify `getMessages`.
 */
export default function RunningStepView({
  step,
}: MainViewComponentProps<AnalysisMainViewItemType.RUNNING>): JSX.Element {
  // We might be rendering `RunningStepView` view on a SUCCEEDED step when it's child step is
  // the viewed dataset and is BLOCKED.
  assertAndContinue(
    isStepInProgress(step) || step.statusCode === PipelineStepStatusCode.SUCCEEDED,
    `Expected to only use this component with ${PipelineStepStatusCode.RUNNING}, ${PipelineStepStatusCode.BLOCKED}, or ${PipelineStepStatusCode.WAITING} steps`,
    {statusCode: step.statusCode, stepId: step.id, stepType: step.definition.stepType}
  );

  const {title, subtitle} = getMessages(step);

  return (
    <div
      className={css(styles.runningStepViewWrapper)}
      {...getTestProps({component: 'RunningStepView', element: 'root'})}
    >
      <div className={css(styles.runningAnimationWrapper)}>
        <AnalysisRunningAnimation />
      </div>

      <p className={css(styles.titleText)}>{title}</p>
      <p className={css(styles.subtitleText)}>{subtitle}</p>
    </div>
  );
}

const styles = StyleSheet.create({
  runningAnimationWrapper: {
    marginBottom: 20,
  },
  runningStepViewWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  subtitleText: {
    ...FONT_PROXIMA_13,
    color: NEAR_BLACK,
  },
  titleText: {
    ...FONT_PROXIMA_16,
    color: NEAR_BLACK,
  },
});
